import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  Stack,
} from "@mui/material";
import TableCompo from "../CommonTable/TableCompo";
import {
  getPendingRedeemRequest,
  getRedeemBalanceRequest,
} from "../../redux/Redeem/redeemSlice";
import { useNavigate } from "react-router-dom";
import { getCarouselRequest } from "../../redux/Dashboard/dashboardSlice";
import CarouselSection from "../CarouselSection/CarouselSlider";
import { getDiamondPendingOrderRequest } from "../../redux/DiamondPoint/DiamondPointSlice";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";

const RedeemHistoryScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { bullionRates, appConfig } = useSelector(
    (state) => state.dashboard || {},
  );
  const { loading, data, error, pendingRedeemList } = useSelector(
    (state) => state.redeem || {},
  );
  const { carousel } = useSelector((state) => state.dashboard || {});
  const { pendingOrder, loading: diamondLoading } = useSelector(
    (state) => state.diamondPoint || {},
  );
  const decimal = appConfig?.decimalPrecision ?? 4;

  const [filter, setFilter] = useState("Holding");

  const getBuyRate = (metalName) => {
    const rate = bullionRates?.find(
      (r) =>
        r.bullion?.metalName === metalName &&
        r.rateType?.toLowerCase() === "buy",
    );
    return rate?.ratePerGram || 0;
  };
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    dispatch(getRedeemBalanceRequest());
    dispatch(getCarouselRequest());
  }, [dispatch]);

  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedType, setSelectedType] = useState("");

  const [bottomCarousel, setBottomCarousel] = React.useState(null);

  const themeColors = useAppTheme();

  const { symbol } = useCurrency();

  const getValidityDays = (validTill) => {
    if (!validTill) return "-";
    const now = new Date().getTime();
    const diff = validTill - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0
      ? `Validity expire in ${days} day${days > 1 ? "s" : ""}`
      : "Expired";
  };

  useEffect(() => {
    if (selectedType === "Diamondpoint") return;
    if (pendingRedeemList === null || Array.isArray(pendingRedeemList)) {
      if (!loading && selectedRow && selectedType) {
        if (Array.isArray(pendingRedeemList)) {
          console.log("pendingRedeemList", pendingRedeemList);
          navigate("/PendingRedeemScreen", {
            state: { id: selectedRow._id, type: selectedType },
          });
        } else if (pendingRedeemList === null) {
          navigate("/redeem", {
            state: { row: selectedRow, type: selectedType },
          });
        }
      }
    }
  }, [pendingRedeemList, loading, selectedRow, selectedType, navigate]);

  useEffect(() => {
    if (selectedType !== "Diamondpoint") return;
    if (pendingOrder === undefined) return; // waiting for API

    if (!diamondLoading && selectedRow) {
      console.log("pendingOrder:", pendingOrder);

      if (pendingOrder && typeof pendingOrder === "object") {
        navigate("/DiamondPointPendingOrder", {
          state: { order: pendingOrder, type: "Diamondpoint" },
        });
      } else {
        navigate("/DiamondPoints", {
          state: { row: selectedRow, type: "Diamondpoint" },
        });
      }
    }
  }, [pendingOrder, diamondLoading, selectedRow, selectedType, navigate]);

  useEffect(() => {
    if (carousel?.data?.length > 0) {
      let top = {},
        mid = {},
        bottom = {};

      carousel.data.forEach((e) => {
        switch (e.name) {
          case "redeem_main_bottom":
            top = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
        }
      });

      setBottomCarousel(bottom);
    } else {
      setBottomCarousel(null);
    }
  }, [carousel]);

  const getTableData = () => {
    if (!data) return [];
    const now = new Date().getTime();

    switch (filter) {
      case "Gift":
        return (data.gifts || [])
          .filter((item) => !item.validTill || item.validTill > now) // remove expired
          .map((item, index) => ({
            id: index + 1,
            _id: item._id,
            metalDetails: `${item.metalName || "-"} - ${
              item.bullion?.karat || "-"
            }K - ${item.bullion?.purity || "-"}`,
            weight: Number(item.weight || 0).toFixed(decimal),
            amount: ((item.weight || 0) * getBuyRate(item.metalName)).toFixed(
              decimal,
            ),
            validity: item.validTill ? getValidityDays(item.validTill) : "-",
            redeemButton: true,
          }));
      case "Holding":
        return (data.holdings || []).map((item, index) => {
          const netGrams = Math.max(
      0,
      (Number(item.availableBalance) || 0) - (Number(item.giftBalance) || 0),
    );
        return {
          id: index + 1,
          _id: item.bullion?._id,
          metalDetails: `${item.metalName || "-"} - ${
            item.bullion?.karat || "-"
          }K - ${item.bullion?.purity || "-"}`,
          weight: netGrams.toFixed(decimal),
          amount: (
            netGrams * getBuyRate(item.metalName)
          ).toFixed(decimal),
          redeemButton: true,
          originalId: item._id,
        };
        });
      case "Sip":
        return (data.sipbalance || []).map((item, index) => {
          const paidCount =
            item.sipInstallments?.filter((i) => i.status === "PAID")?.length ||
            0;
          const totalMonths = item.noOfMonths || 0;
          return {
            id: index + 1,
            _id: item._id,
            plan: item.sipplan?.name || "-",
            metalDetails: `${item.metalName || "-"} - ${
              item.bullion?.karat || "-"
            }K - ${item.bullion?.purity || "-"}`,
            weight: Number(item.weightbalance || 0).toFixed(decimal),
            amount: (
              (item.weightbalance || 0) * getBuyRate(item.metalName)
            ).toFixed(decimal),
            paidInfo: `${paidCount}/${totalMonths}`,
            tag: item.tag || "-",
            redeemButton: true,
          };
        });
      case "Diamondpoint":
        return (data.diamondpoints || [])
          .filter((item) => !item.expireAt || item.expireAt > now) // remove expired
          .map((item, index) => ({
            id: index + 1,
            ...item,
            _id: item._id,
            points: item.points || 0,
            source: item.source || "Diamond Points record",
            validity: item.expireAt ? getValidityDays(item.expireAt) : "-",
            redeemButton: true,
          }));
      default:
        return [];
    }
  };

  const getColumns = () => {
    switch (filter) {
      case "Holding":
        return [
          {
            field: "metalDetails",
            headerName: "Metal (Karat - Purity)",
            hide: false,
            minWidth: 150,
          },
          {
            field: "weight",
            headerName: "Weight (gms)",
            hide: false,
            minWidth: 150,
          },
          {
            field: "amount",
            headerName: `Amount (${symbol})`,
            hide: false,
            minWidth: 150,
          },
          {
            field: "redeemButton",
            headerName: "Redeem",
            flex: 1,
            render: (val, row) => (
              <Button
                variant="contained"
                sx={{
                  background: themeColors.mediumContainer,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.scaffold}`,
                  textTransform: "none",
                  borderRadius: "20px",
                }}
                size="small"
                onClick={() => {
                  setSelectedRow(row);
                  setSelectedType(filter);
                  dispatch(
                    getPendingRedeemRequest({ id: row._id, type: filter }),
                  );
                }}
              >
                Redeem
              </Button>
            ),
          },
        ];
      case "Gift":
        return [
          {
            field: "metalDetails",
            headerName: "Gift Metal (Karat - Purity)",
            hide: false,
            minWidth: 150,
          },
          {
            field: "weight",
            headerName: "Gift Weight (gms)",
            hide: false,
            minWidth: 150,
          },
          {
            field: "amount",
            headerName: `Gift Amount (${symbol})`,
            hide: false,
            minWidth: 150,
          },
          {
            field: "validity",
            headerName: "Validity",
            hide: false,
            minWidth: 150,
          },
          {
            field: "redeemButton",
            headerName: "Redeem",
            flex: 1,
            render: (val, row) => (
              <Button
                variant="contained"
                sx={{
                  background: themeColors.mediumContainer,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.scaffold}`,
                  textTransform: "none",
                  borderRadius: "20px",
                }}
                size="small"
                onClick={() => {
                  setSelectedRow(row);
                  setSelectedType(filter);
                  dispatch(
                    getPendingRedeemRequest({ id: row._id, type: filter }),
                  );
                }}
              >
                Redeem
              </Button>
            ),
          },
        ];
      case "Sip":
        return [
          { field: "plan", headerName: "SIP Plan", hide: false, minWidth: 150 },
          {
            field: "metalDetails",
            headerName: "Metal (Karat - Purity)",
            hide: false,
            minWidth: 150,
          },
          {
            field: "weight",
            headerName: "Weight (gms)",
            hide: false,
            minWidth: 150,
          },
          {
            field: "amount",
            headerName: `Amount (${symbol})`,
            hide: false,
            minWidth: 150,
          },
          {
            field: "paidInfo",
            headerName: "Paid/Total",
            hide: false,
            minWidth: 150,
          },
          { field: "tag", headerName: "Tag", hide: false, minWidth: 150 },
          {
            field: "redeemButton",
            headerName: "Redeem",
            flex: 1,
            render: (val, row) => (
              <Button
                variant="contained"
                sx={{
                  background: themeColors.mediumContainer,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.scaffold}`,
                  textTransform: "none",
                  borderRadius: "20px",
                }}
                size="small"
                onClick={() => {
                  setSelectedRow(row);
                  setSelectedType(filter);
                  dispatch(
                    getPendingRedeemRequest({ id: row._id, type: filter }),
                  );
                }}
              >
                Redeem
              </Button>
            ),
          },
        ];
      case "Diamondpoint":
        return [
          {
            field: "points",
            headerName: "Diamond Points",
            hide: false,
            minWidth: 150,
          },
          { field: "source", headerName: "Source", minWidth: 150 },
          { field: "validity", headerName: "Validity", minWidth: 150 },
          {
            field: "redeemButton",
            headerName: "Redeem",
            flex: 1,
            render: (val, row) => (
              <Button
                variant="contained"
                sx={{
                  background: themeColors.mediumContainer,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.scaffold}`,
                  textTransform: "none",
                  borderRadius: "20px",
                }}
                size="small"
                onClick={() => {
                  setSelectedRow(row);
                  setSelectedType("Diamondpoint");

                  dispatch(getDiamondPendingOrderRequest({ id: row._id }));
                }}
              >
                Redeem
              </Button>
            ),
          },
        ];
      default:
        return [];
    }
  };

  const tableData = getTableData();
  const paginatedData = tableData.slice(
    page * limit,
    page * limit + limit
  );

  console.log(filter, tableData);
  const handleTableChange = (type, payload) => {
  if (type === "changePage") {
    setPage(payload.page);
  }

  if (type === "changeRowsPerPage") {
    setLimit(payload.rowsPerPage);
    setPage(0);
  }
};


  return (
    <Box
      sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 8 }}
    >
      <Card
        sx={{
          width: "98%",
          maxWidth: "1300px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          borderRadius: 3,
          background: themeColors.scaffoldGradientColors,
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontWeight: 700,
              //color: "#333",
              fontFamily: "Aeonik",
              color: themeColors.primary,
            }}
          >
            Redeem History
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            {[
              { label: "Holdings", value: "Holding" },
              { label: "Gifts", value: "Gift" },
              { label: "SIP", value: "Sip" },
              { label: "Diamond Points", value: "Diamondpoint" },
            ].map((btn) => (
              <Button
                key={btn.value}
                variant={filter === btn.value ? "contained" : "outlined"}
                size="small"
                onClick={() => setFilter(btn.value)}
                sx={{
                  textTransform: "none",
                  borderRadius: "20px",
                  px: 2,
                  fontWeight: 600,
                  background: themeColors.primary,
                  color: themeColors.text,
                  "&:hover": {
                    background: themeColors.svgColor,
                  },
                  border: `1px solid ${themeColors.scaffold}`,
                }}
              >
                {btn.label}
              </Button>
            ))}
          </Stack>

          {loading ? (
            <Box sx={{ textAlign: "center", mt: 10 }}>
              <CircularProgress color="primary" />
              <Typography variant="body2" mt={2}>
                Loading redeem balance...
              </Typography>
            </Box>
          ) : error ? (
            <Typography color="error" textAlign="center" mt={5}>
              {error}
            </Typography>
          ) : (
            <Box sx={{ height: "70vh", width: "100%", minWidth: 0, overflow: "auto" }}>
              <TableCompo
                title={`${
                  filter.charAt(0).toUpperCase() + filter.slice(1)
                } History`}
                columns={getColumns()}
                data={paginatedData}
                page={page}
                limit={limit}
                totalCount={tableData.length}
                onTableChange={handleTableChange}
                loading={loading}
              />
            </Box>
          )}

          {bottomCarousel && (
            <CarouselSection
              slides={bottomCarousel.slides}
              height={bottomCarousel.height}
              showNavigator={bottomCarousel.showNavigator}
              duration={bottomCarousel.duration}
              enabled={bottomCarousel.enabled}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RedeemHistoryScreen;
